import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

const translations = {
  nl: {
    // Navigation
    dashboard: 'Dashboard',
    loans: 'Leningen',
    realEstate: 'Vastgoed',
    cashflow: 'Cashflow',
    users: 'Gebruikers',
    creditApplication: 'Kredietaanvraag',
    legalOwners: 'Juridische Eigenaren',
    logout: 'Uitloggen',
    
    // Auth
    login: 'Inloggen',
    register: 'Registreren',
    email: 'E-mailadres',
    password: 'Wachtwoord',
    name: 'Naam',
    loginWithGoogle: 'Inloggen met Google',
    orContinueWith: 'of ga verder met',
    dontHaveAccount: 'Nog geen account?',
    alreadyHaveAccount: 'Heb je al een account?',
    
    // Dashboard KPIs
    financeKeyFigures: 'Financiële Kengetallen',
    realEstateKeyFigures: 'Vastgoed Kengetallen',
    sustainabilityKeyFigures: 'Duurzaamheid',
    totalLoanAmount: 'Totaal Leningbedrag',
    loanToValue: 'Loan-to-Value',
    interestCoverageRatio: 'Interest Coverage Ratio',
    debtServiceCoverageRatio: 'Debt Service Coverage Ratio',
    debtYield: 'Debt Yield',
    surplus: 'Surplus',
    totalMarketValue: 'Totale Marktwaarde',
    totalGrossRental: 'Totale Bruto Huur',
    totalNetRental: 'Totale Netto Huur',
    vacancyRate: 'Leegstandspercentage',
    energyLabelDistribution: 'Energielabel Verdeling',
    
    // Loans
    addLoan: 'Lening Toevoegen',
    editLoan: 'Lening Bewerken',
    loanName: 'Naam Lening',
    loanAmount: 'Leningbedrag',
    interestRate: 'Rentepercentage',
    repaymentRate: 'Aflossingspercentage',
    lenderName: 'Kredietverstrekker',
    expiryDate: 'Einddatum',
    interestRenewalDate: 'Rente Herzieningsdatum',
    interestAmount: 'Rentebedrag',
    repaymentAmount: 'Aflossingsbedrag',
    linkedProperties: 'Gekoppelde Panden',
    
    // Real Estate
    addProperty: 'Vastgoed Toevoegen',
    editProperty: 'Vastgoed Bewerken',
    address: 'Adres',
    street: 'Straat',
    houseNumber: 'Huisnummer',
    houseNumberSupplement: 'Huisnummer Toevoeging',
    postalCode: 'Postcode',
    city: 'Stad',
    bagPandId: 'BAG Pand ID',
    bagVboId: 'BAG VBO ID',
    marketValue: 'Marktwaarde',
    totalSqmBvo: 'Totaal m² BVO',
    totalSqmVvo: 'Totaal m² VVO',
    assetClass: 'Vastgoedtype',
    grossRentalIncome: 'Bruto Huurinkomsten (€/jaar)',
    netRentalIncome: 'Netto Huurinkomsten (€/jaar)',
    energyLabel: 'Energielabel',
    capexPercentage: 'CAPEX %',
    
    // Asset classes
    residential: 'Residentieel',
    office: 'Kantoor',
    retail: 'Retail',
    lightIndustrial: 'Lichte Industrie',
    combination: 'Combinatie',
    logistics: 'Logistiek',
    other: 'Overig',
    
    // Tenants
    tenants: 'Huurders',
    addTenant: 'Huurder Toevoegen',
    editTenant: 'Huurder Bewerken',
    tenantName: 'Naam Huurder',
    chamberOfCommerce: 'KvK Nummer',
    startDate: 'Startdatum',
    endDate: 'Einddatum',
    optionPeriods: 'Optieperiodes',
    rentalAmount: 'Huurbedrag (€/jaar)',
    incentives: 'Incentives',
    sqmRented: 'Gehuurde m²',
    rentPerSqm: 'Huur per m²',
    vacancy: 'Leegstand',
    
    // Legal Owners / Parties
    parties: 'Partijen',
    addParty: 'Partij Toevoegen',
    editParty: 'Partij Bewerken',
    partyName: 'Naam Partij',
    partyType: 'Type Partij',
    addLegalOwner: 'Eigenaar Toevoegen',
    editLegalOwner: 'Eigenaar Bewerken',
    ownerName: 'Naam Eigenaar',
    ownerType: 'Type Eigenaar',
    privateOwner: 'Particulier',
    bvOwner: 'B.V.',
    kvkNumber: 'KvK Nummer',
    ownershipPercentage: 'Eigendomspercentage',
    selectOwner: 'Selecteer Eigenaar',
    filterByOwner: 'Filter op Eigenaar',
    allOwners: 'Alle Eigenaren',
    filterByParty: 'Filter op Partij',
    allParties: 'Alle Partijen',
    resetFilter: 'Filter Resetten',
    roles: 'Rollen',
    contactEmail: 'E-mailadres',
    contactPhone: 'Telefoonnummer',
    borrowers: 'Kredietnemers',
    hms: 'HMS',
    borg: 'Borg',
    addBorrower: 'Kredietnemer Toevoegen',
    addHms: 'HMS Toevoegen',
    addBorg: 'Borg Toevoegen',
    
    // Credit Application
    newCreditApplication: 'Nieuwe Kredietaanvraag',
    editCreditApplication: 'Kredietaanvraag Bewerken',
    applicationName: 'Naam Aanvraag',
    loanAmountRequested: 'Gevraagd Leningbedrag',
    equityAmount: 'Eigen Vermogen',
    subordinatedLoan: 'Achtergestelde Lening',
    hasSubordinatedLoan: 'Achtergestelde Lening als EV?',
    subordinatedAmount: 'Achtergesteld Bedrag',
    subordinatedInterestRate: 'Rente Achtergesteld %',
    subordinatedAmortization: 'Aflossing Achtergesteld %',
    expectedGrossRental: 'Verwachte Bruto Huur',
    addPropertyToApplication: 'Vastgoed Toevoegen',
    
    // Application Status
    status: 'Status',
    draft: 'Concept',
    submitted: 'Ingediend',
    underReview: 'In Beoordeling',
    approved: 'Goedgekeurd',
    rejected: 'Afgewezen',
    submitApplication: 'Aanvraag Indienen',
    approveApplication: 'Goedkeuren',
    rejectApplication: 'Afwijzen',
    
    // Cashflow
    cashflowOverview: 'Cashflow Overzicht',
    year: 'Jaar',
    occupancy: 'Bezettingsgraad',
    outstanding: 'Uitstaand',
    gri: 'Bruto Huur (GRI)',
    capex: 'CAPEX',
    nri: 'Netto Huur (NRI)',
    
    // Tables
    top5Properties: 'Top 5 Vastgoed',
    top5Tenants: 'Top 5 Huurders',
    loansByExpiry: 'Leningen per Afloopdatum',
    
    // Actions
    save: 'Opslaan',
    cancel: 'Annuleren',
    delete: 'Verwijderen',
    edit: 'Bewerken',
    view: 'Bekijken',
    create: 'Aanmaken',
    search: 'Zoeken',
    filter: 'Filteren',
    export: 'Exporteren',
    
    // Messages
    loading: 'Laden...',
    noData: 'Geen gegevens beschikbaar',
    confirmDelete: 'Weet je zeker dat je dit wilt verwijderen?',
    savedSuccessfully: 'Succesvol opgeslagen',
    deletedSuccessfully: 'Succesvol verwijderd',
    errorOccurred: 'Er is een fout opgetreden',
    
    // User management
    userManagement: 'Gebruikersbeheer',
    role: 'Rol',
    admin: 'Beheerder',
    investor: 'Investeerder',
    changeRole: 'Rol Wijzigen',
    
    // Misc
    notes: 'Notities',
    image: 'Afbeelding',
    uploadImage: 'Afbeelding Uploaden',
    welcome: 'Welkom',
    goodMorning: 'Goedemorgen',
    goodAfternoon: 'Goedemiddag',
    goodEvening: 'Goedenavond',
    viewCashflow: 'Bekijk Cashflow',
  },
  en: {
    // Navigation
    dashboard: 'Dashboard',
    loans: 'Loans',
    realEstate: 'Real Estate',
    cashflow: 'Cashflow',
    users: 'Users',
    creditApplication: 'Credit Application',
    legalOwners: 'Legal Owners',
    logout: 'Logout',
    
    // Auth
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    loginWithGoogle: 'Login with Google',
    orContinueWith: 'or continue with',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    
    // Dashboard KPIs
    financeKeyFigures: 'Finance Key Figures',
    realEstateKeyFigures: 'Real Estate Key Figures',
    sustainabilityKeyFigures: 'Sustainability',
    totalLoanAmount: 'Total Loan Amount',
    loanToValue: 'Loan-to-Value',
    interestCoverageRatio: 'Interest Coverage Ratio',
    debtServiceCoverageRatio: 'Debt Service Coverage Ratio',
    debtYield: 'Debt Yield',
    surplus: 'Surplus',
    totalMarketValue: 'Total Market Value',
    totalGrossRental: 'Total Gross Rental',
    totalNetRental: 'Total Net Rental',
    vacancyRate: 'Vacancy Rate',
    energyLabelDistribution: 'Energy Label Distribution',
    
    // Loans
    addLoan: 'Add Loan',
    editLoan: 'Edit Loan',
    loanName: 'Loan Name',
    loanAmount: 'Loan Amount',
    interestRate: 'Interest Rate',
    repaymentRate: 'Repayment Rate',
    lenderName: 'Lender Name',
    expiryDate: 'Expiry Date',
    interestRenewalDate: 'Interest Renewal Date',
    interestAmount: 'Interest Amount',
    repaymentAmount: 'Repayment Amount',
    linkedProperties: 'Linked Properties',
    
    // Real Estate
    addProperty: 'Add Property',
    editProperty: 'Edit Property',
    address: 'Address',
    street: 'Street',
    houseNumber: 'House Number',
    houseNumberSupplement: 'House Number Supplement',
    postalCode: 'Postal Code',
    city: 'City',
    bagPandId: 'BAG Pand ID',
    bagVboId: 'BAG VBO ID',
    marketValue: 'Market Value',
    totalSqmBvo: 'Total m² BVO',
    totalSqmVvo: 'Total m² VVO',
    assetClass: 'Asset Class',
    grossRentalIncome: 'Gross Rental Income (€/year)',
    netRentalIncome: 'Net Rental Income (€/year)',
    energyLabel: 'Energy Label',
    capexPercentage: 'CAPEX %',
    
    // Asset classes
    residential: 'Residential',
    office: 'Office',
    retail: 'Retail',
    lightIndustrial: 'Light Industrial',
    combination: 'Combination',
    logistics: 'Logistics',
    other: 'Other',
    
    // Tenants
    tenants: 'Tenants',
    addTenant: 'Add Tenant',
    editTenant: 'Edit Tenant',
    tenantName: 'Tenant Name',
    chamberOfCommerce: 'Chamber of Commerce',
    startDate: 'Start Date',
    endDate: 'End Date',
    optionPeriods: 'Option Periods',
    rentalAmount: 'Rental Amount (€/year)',
    incentives: 'Incentives',
    sqmRented: 'Rented m²',
    rentPerSqm: 'Rent per m²',
    vacancy: 'Vacancy',
    
    // Legal Owners / Parties
    parties: 'Parties',
    addParty: 'Add Party',
    editParty: 'Edit Party',
    partyName: 'Party Name',
    partyType: 'Party Type',
    addLegalOwner: 'Add Owner',
    editLegalOwner: 'Edit Owner',
    ownerName: 'Owner Name',
    ownerType: 'Owner Type',
    privateOwner: 'Private',
    bvOwner: 'B.V.',
    kvkNumber: 'KvK Number',
    ownershipPercentage: 'Ownership %',
    selectOwner: 'Select Owner',
    filterByOwner: 'Filter by Owner',
    allOwners: 'All Owners',
    filterByParty: 'Filter by Party',
    allParties: 'All Parties',
    resetFilter: 'Reset Filter',
    roles: 'Roles',
    contactEmail: 'Email',
    contactPhone: 'Phone',
    borrowers: 'Borrowers',
    hms: 'HMS',
    borg: 'Guarantor',
    addBorrower: 'Add Borrower',
    addHms: 'Add HMS',
    addBorg: 'Add Guarantor',
    
    // Credit Application
    newCreditApplication: 'New Credit Application',
    editCreditApplication: 'Edit Credit Application',
    applicationName: 'Application Name',
    loanAmountRequested: 'Loan Amount Requested',
    equityAmount: 'Equity Amount',
    subordinatedLoan: 'Subordinated Loan',
    hasSubordinatedLoan: 'Subordinated Loan as Equity?',
    subordinatedAmount: 'Subordinated Amount',
    subordinatedInterestRate: 'Subordinated Interest %',
    subordinatedAmortization: 'Subordinated Amortization %',
    expectedGrossRental: 'Expected Gross Rental',
    addPropertyToApplication: 'Add Property',
    
    // Application Status
    status: 'Status',
    draft: 'Draft',
    submitted: 'Submitted',
    underReview: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
    submitApplication: 'Submit Application',
    approveApplication: 'Approve',
    rejectApplication: 'Reject',
    
    // Cashflow
    cashflowOverview: 'Cashflow Overview',
    year: 'Year',
    occupancy: 'Occupancy',
    outstanding: 'Outstanding',
    gri: 'Gross Rental (GRI)',
    capex: 'CAPEX',
    nri: 'Net Rental (NRI)',
    
    // Tables
    top5Properties: 'Top 5 Properties',
    top5Tenants: 'Top 5 Tenants',
    loansByExpiry: 'Loans by Expiry',
    
    // Actions
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    
    // Messages
    loading: 'Loading...',
    noData: 'No data available',
    confirmDelete: 'Are you sure you want to delete this?',
    savedSuccessfully: 'Saved successfully',
    deletedSuccessfully: 'Deleted successfully',
    errorOccurred: 'An error occurred',
    
    // User management
    userManagement: 'User Management',
    role: 'Role',
    admin: 'Admin',
    investor: 'Investor',
    changeRole: 'Change Role',
    
    // Misc
    notes: 'Notes',
    image: 'Image',
    uploadImage: 'Upload Image',
    welcome: 'Welcome',
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    viewCashflow: 'View Cashflow',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('investdash_language') || 'nl';
  });

  useEffect(() => {
    localStorage.setItem('investdash_language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'nl' ? 'en' : 'nl');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
